import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { ThemeProvider as SCThemeProvider } from 'styled-components';
import { lightTheme, darkTheme } from '../styles/theme';

interface ThemeContextType {
  isDark: boolean;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType>({
  isDark: false,
  toggleTheme: () => {},
});

export const useTheme = () => useContext(ThemeContext);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem('dt-theme');
    return saved === 'dark';
  });

  const reEnableTimer = useRef<number | null>(null);

  useEffect(() => {
    localStorage.setItem('dt-theme', isDark ? 'dark' : 'light');
  }, [isDark]);

  const toggleTheme = useCallback(() => {
    const root = document.documentElement;
    // Mark the swap window so the global CSS rule animates only color-related
    // properties on every element for ~360ms — gives a smooth fade between
    // themes without re-triggering hover/transform animations everywhere.
    root.setAttribute('data-theme-transitioning', 'on');
    if (reEnableTimer.current) window.clearTimeout(reEnableTimer.current);
    setIsDark((prev) => !prev);
    reEnableTimer.current = window.setTimeout(() => {
      root.removeAttribute('data-theme-transitioning');
      reEnableTimer.current = null;
    }, 480);
  }, []);

  useEffect(() => () => {
    if (reEnableTimer.current) window.clearTimeout(reEnableTimer.current);
  }, []);

  const ctxValue = useMemo(() => ({ isDark, toggleTheme }), [isDark, toggleTheme]);

  return (
    <ThemeContext.Provider value={ctxValue}>
      <SCThemeProvider theme={isDark ? darkTheme : lightTheme}>{children}</SCThemeProvider>
    </ThemeContext.Provider>
  );
};
